import { gql } from "../__generated__";

/**
 * GraphQL documents shared by Venus and Neptune, defined exactly once.
 *
 * These operations/fragments used to be copy-pasted in both apps (codegen was
 * per-app), which let their selections silently drift — a field added in one
 * app's copy but not the other's meant a feature worked in Venus and was a dead
 * click in Neptune. With the single shared codegen registry
 * (`@lytra/frontend-common/__generated__`), an operation name may exist only
 * once across the monorepo, so shared documents live here and the apps import
 * them.
 */

export const MESSAGE_REFERENCES_FRAGMENT = gql(`
  fragment MessageReferencesFragment on Message {
    id
    sources {
      id
      selected
      type
      title
      preview
      customerRequestId
      externalLink
      sourceConversationId
      sourceConversationMessageId
      sourceConversationExternalId
      sourceContactContextId
      sourceMessageEmailMseMessageId
      sourceMessageSummary
      sourceDocumentId
      sourceDocumentPageNumber
      sourceDatabaseTableId
      sourceDatabaseRowPkValues
      pastCase {
        caseNumber
        date
        text
      }
      sourceDatabaseColumnName
      scopeSourceLabel
      scopeExpectedLabel
      scopeFactLabel
      contentDate
      citations {
        index
        paragraphIndices
        quoteStart
      }
    }
  }
`);

// Every language pick goes through this (spec rule 3.1). The backend translates
// the answer, persists it as a real TRANSLATION revision and moves the revision
// pointer to it; repeat switches are recognised by a fingerprint on the lineage
// root and cost a round-trip rather than a fresh translation (rule 3.2).
// Resolves with null when the backend has nothing to hand back yet.
export const TRANSLATE_PROPOSED_ANSWER = gql(`
    mutation TranslateProposedAnswer($messageId: UUID!, $targetLanguageCode: String!) {
        messageTranslateProposedAnswer(messageId: $messageId, targetLanguageCode: $targetLanguageCode)
    }
`);

export const GET_EFFECTIVE_LANGUAGE = gql(`
  query GetEffectiveLanguage {
    effectiveLanguage
  }
`);

// A service agent adds, edits or removes one attribute value (ADR-0065). The backend
// expands an added value, rejects a removed one for good, and schedules a debounced
// regeneration itself, so callers must NOT also kick off a generation.
//
// Each answers with the conversation's attributes as they now are. Apollo normalises
// the Conversation by id, so the hosts' own queries show the new list without a
// refetch. The selection covers every attribute field either host reads.
export const ATTRIBUTE_EDIT_RESULT_FRAGMENT = gql(`
  fragment AttributeEditResultFields on AttributeEditResult {
    conversation {
      id
      attributes {
        id
        key
        value
        title
        externalLink
        sortOrder
        source
        createdAt
        expandedFrom
        recognized
      }
    }
  }
`);

export const ADD_ATTRIBUTE_VALUE = gql(`
  mutation AddAttributeValue($messageId: UUID!, $key: String!, $value: String!) {
    messageAddAttributeValue(messageId: $messageId, key: $key, value: $value) {
      changed
      ...AttributeEditResultFields
    }
  }
`);

export const REMOVE_ATTRIBUTE_VALUE = gql(`
  mutation RemoveAttributeValue($messageId: UUID!, $attributeId: UUID!) {
    messageRemoveAttributeValue(messageId: $messageId, attributeId: $attributeId) {
      changed
      ...AttributeEditResultFields
    }
  }
`);

export const REPLACE_ATTRIBUTE_VALUE = gql(`
  mutation ReplaceAttributeValue($messageId: UUID!, $attributeId: UUID!, $value: String!) {
    messageReplaceAttributeValue(messageId: $messageId, attributeId: $attributeId, value: $value) {
      changed
      ...AttributeEditResultFields
    }
  }
`);

// The attribute kinds a service agent can add a value of, in card order.
export const CONVERSATION_ATTRIBUTE_KINDS = gql(`
  query ConversationAttributeKinds {
    conversationAttributeKinds {
      key
      title
    }
  }
`);
